import React, { useState, useEffect } from 'react'
import {
  Button,
  Container,
  TextBody,
  TextTitle,
  Link,
  FieldEmail,
  FieldText,
  Checkbox,
  Popup,
  Icon,
  Icon64
} from '@pmi/pmi-iqos-ds-react'
import {
  constants,
  analytics,
  loadExternalScript,
  htmlTranslation,
  registerForm,
  registerParam,
  validateForm,
  validateParam,
  unregisterForm,
  unegisterParam,
  getRoute,
  Routes,
  Fragments,
  Grecaptcha,
  GRecaptchaStyleOverride,
  Sites
} from '@pmi/pmi-iqos-shared-react'

const ModalNotifyMe = ({
  isOpen,
  isAuthenticated,
  product,
  selectedOptionIndex,
  defaultName,
  defaultEmail,
  translations,
  handleOnNotifyMe,
  onClose,
  line
}) => {
  const formFieldNames = {
    name: 'name',
    email: 'email',
    termsCheckbox: 'termsCheckbox'
  }
  const [email, setEmail] = useState(defaultEmail || '')
  const [name, setName] = useState(defaultName || '')
  const [termsCheckbox, setTermsCheckbox] = useState(false)
  const [loading, setLoading] = useState(false)
  const [emailErrors, setEmailErrors] = useState([])
  const [nameErrors, setNameErrors] = useState([])
  const [ready, setReady] = useState(false)
  const [recaptchaData, setRecaptchaData] = useState({})
  const [isRecaptchaValid, setIsRecaptchaValid] = useState(true)
  const [recaptchaFail, setRecaptchaFail] = useState(false)
  const [isRecaptchaReady, seIsRecaptchaReady] = useState(false)
  const [success, setSuccess] = useState(line)
  const formId = registerForm()
  const validators = {
    [formFieldNames.email]: registerParam(
      [
        {
          type: 'email',
          description: translations.tk_modalNotifyMeEmailValidation
        },
        {
          type: 'emailAlias',
          description: translations.tk_modalNotifyMeEmailAliasValidation
        }
      ],
      false,
      formId,
      email,
      errors => {
        setEmailErrors(errors)
      }
    ),
    [formFieldNames.name]: registerParam(
      [
        {
          type: 'length',
          rule: 'max_30',
          description: translations.tk_modalNotifyMeNameValidation1
        },
        {
          type: 'name',
          description: translations.tk_modalNotifyMeNameValidation2
        },
        {
          type: 'not_start',
          rule: ' ',
          description: translations.tk_modalNotifyMeNameValidation3
        }
      ],
      false,
      formId,
      name,
      errors => {
        setNameErrors(errors)
      }
    )
  }

  useEffect(() => {
    if (!ready) {
      setReady(true)
    }
    return () => {
      unregisterForm(formId)
      Object.keys(formFieldNames).forEach(fieldNameKey =>
        unegisterParam(validators[formFieldNames[fieldNameKey].id])
      )
    }
  }, [])

  useEffect(() => {
    isOpen &&
      !isAuthenticated &&
      loadExternalScript(
        'recaptchaScriptId',
        `${constants.RECAPTCHA_BASE_URL}api.js?render=${constants.RECAPTCHA_ZYN_V3_SITE_KEY}`,
        () => {
          seIsRecaptchaReady(true)
        }
      )
  }, [isOpen])

  const handleChangeEmail = e => {
    const errors = validateParam(
      e.target.value,
      validators[formFieldNames.email],
      formId
    )
    setEmailErrors(errors)
    setEmail(e.target.value)
  }

  const handleChangeName = e => {
    const errors = validateParam(
      e.target.value,
      validators[formFieldNames.name],
      formId
    )
    setNameErrors(errors)
    setName(e.target.value)
  }

  const handleChangeTermsCheckbox = e => {
    setTermsCheckbox(!termsCheckbox)
  }

  const handleNotifyMePressed = async e => {
    e.preventDefault()
    if (handleOnNotifyMe && validateForm(formId) && isRecaptchaValid) {
      setLoading(true)
      analytics.notifyMe(
        'Click',
        isAuthenticated
          ? 'Post - Pop-up - Register'
          : 'Pre - Pop-up - Register',
        product.options[selectedOptionIndex].variantId,
        product.options[selectedOptionIndex].variantName
      )
      const res = await handleOnNotifyMe(
        isAuthenticated ? null : { name: name, email: email },
        product,
        selectedOptionIndex,
        recaptchaData
      )
      if (res.success) {
        setSuccess(true)
      } else if (res.captchaError) {
        setRecaptchaFail(true)
      } else if (res.error) {
        const label =
          (isAuthenticated
            ? 'Post - Pop-up - Error Message - '
            : 'Pre - Pop-up - Error Message - ') + res.error
        analytics.notifyMe('Click', label)
      }
      setLoading(false)
    }
  }

  const handleOnLoginClick = () => {
    analytics.notifyMe('Click', 'Pre - Pop-up - Login')
  }

  const handleOnEditProfileClick = () => {
    analytics.notifyMe('Click', 'Post - Pop-up - Modify')
  }

  const handleOnClose = () => {
    if (!success) {
      analytics.notifyMe(
        'Click',
        isAuthenticated ? 'Post - Pop-up - Close' : 'Pre - Pop-up - Close'
      )
    }
    onClose()
  }

  const modalNotifyMeSuccessMessage = htmlTranslation(
    translations,
    line
      ? 'tk_modalNotifyMeSuccessMessageLine'
      : 'tk_modalNotifyMeSuccessMessage'
  )
  if (product) {
    modalNotifyMeSuccessMessage.value = modalNotifyMeSuccessMessage.value
      .replace('[[ product_name ]]', product.name)
      .replace(
        '[[ variant_short_name]]',
        product.options[selectedOptionIndex].shortVariantName || ''
      )
  }

  return (
    <Popup
      isOpen={isOpen}
      onCloseHandler={handleOnClose}
      data-testing-prefix="NotifyMe">
      <GRecaptchaStyleOverride />
      <Container
        fullWidth
        display={Container.displays.flex}
        flexDirection={Container.flexDirections.column}>
        {success ? (
          <Container
            fullWidth
            display={Container.displays.flex}
            flexDirection={Container.flexDirections.column}
            alignItems={Container.alignItemsValues.center}
            justifyContent={Container.justifyContentValues.center}
            pt="6">
            <Icon64 image={Icon64.images.confirmation} size={Icon64.sizes.xl} />
            <Container pt="14">
              <TextTitle heading={TextTitle.headings.h5}>
                {line
                  ? translations.tk_modalNotifyMeSuccessTitleLine
                  : translations.tk_modalNotifyMeSuccessTitle}
              </TextTitle>
            </Container>
            <Container pt="4">
              <TextBody
                size={TextBody.sizes.s}
                alignment={TextBody.alignments.center}
                {...modalNotifyMeSuccessMessage}
              />
            </Container>
          </Container>
        ) : (
          <Container
            fullWidth
            display={Container.displays.flex}
            flexDirection={Container.flexDirections.column}
            alignItems={Container.alignItemsValues.center}
            justifyContent={[
              Container.justifyContentValues.start,
              Container.justifyContentValues.center
            ]}
            pt="6">
            <TextTitle heading={TextTitle.headings.h4}>
              {translations.tk_modalNotifyMeTitle}
            </TextTitle>
            <Container pt="2">
              <TextBody
                size={TextBody.sizes.s}
                {...htmlTranslation(translations, 'tk_modalNotifyMeSubtitle')}
              />
            </Container>
            {isAuthenticated ? (
              <Container fullWidth pt="6" px={['0', '6']}>
                <Container
                  display={Container.displays.flex}
                  flexDirection={Container.flexDirections.column}
                  pt="4">
                  <Container pl="8">
                    <TextBody
                      size={TextBody.sizes.s}
                      style={TextBody.styles.grayedout}
                      {...htmlTranslation(translations, 'tk_modalNotifyMeName')}
                    />
                  </Container>
                  <Container
                    fullWidth
                    display={Container.displays.flex}
                    alignItems={Container.alignItemsValues.center}>
                    <Icon size={Icon.sizes.xs} image={Icon.images.user} />
                    <Container px="4" fullWidth>
                      <TextTitle
                        heading={TextTitle.headings.h6}
                        data-testing="NotifyMe_TextTitle_Name">
                        {name}
                      </TextTitle>
                    </Container>
                  </Container>
                </Container>
                <Container
                  display={Container.displays.flex}
                  flexDirection={Container.flexDirections.column}
                  pt="6">
                  <Container pl="8">
                    <TextBody
                      size={TextBody.sizes.s}
                      style={TextBody.styles.grayedout}
                      {...htmlTranslation(
                        translations,
                        'tk_modalNotifyMeEmail'
                      )}
                    />
                  </Container>
                  <Container
                    fullWidth
                    display={Container.displays.flex}
                    alignItems={Container.alignItemsValues.center}>
                    <Icon size={Icon.sizes.xs} image={Icon.images.mail} />
                    <Container px="4" fullWidth>
                      <TextTitle
                        heading={TextTitle.headings.h6}
                        data-testing="NotifyMe_TextTitle_Email">
                        {email}
                      </TextTitle>
                    </Container>
                  </Container>
                </Container>
              </Container>
            ) : (
              <Container fullWidth pt="8">
                <FieldText
                  value={name}
                  name={formFieldNames.name}
                  label={translations.tk_modalNotifyMeName}
                  placeholder={translations.tk_modalNotifyMeName}
                  onChangeHandler={handleChangeName}
                  disabled={loading}
                  errors={nameErrors}
                  data-testing="NotifyMe_FieldText_Name"
                />
                <Container fullWidth pt="6">
                  <FieldEmail
                    value={email}
                    name={formFieldNames.email}
                    label={translations.tk_modalNotifyMeEmail}
                    placeholder={translations.tk_modalNotifyMeEmail}
                    onChangeHandler={handleChangeEmail}
                    disabled={loading}
                    errors={emailErrors}
                    data-testing="NotifyMe_FieldEmail_Email"
                  />
                </Container>
              </Container>
            )}
            <Container pt="10" fullWidth>
              <Container
                fullWidth
                display={Container.displays.flex}
                alignItems={Container.alignItemsValues.center}>
                <Container>
                  <Checkbox
                    fullWidth
                    checked={termsCheckbox}
                    name={formFieldNames.termsCheckbox}
                    template={
                      <Container pl="2">
                        <TextBody
                          size={TextBody.sizes.s}
                          {...htmlTranslation(
                            translations,
                            'tk_modalNotifyMeTermsAndConditions'
                          )}
                        />
                      </Container>
                    }
                    onChangeHandler={handleChangeTermsCheckbox}
                    disabled={loading}
                    data-testing="NotifyMe_Checkbox_Terms"
                  />
                </Container>
              </Container>
            </Container>
            {isRecaptchaReady && !isAuthenticated && (
              <Container pt="4">
                <Grecaptcha
                  disabled={loading}
                  action={constants.STOCK_NOTIFICATION_GRECAPTCHA_ACTION}
                  onRecaptchaData={setRecaptchaData}
                  onValidityChange={setIsRecaptchaValid}
                  recaptchaFail={recaptchaFail}
                  isZyn={true}
                />
              </Container>
            )}
            <Container py={['8', '6']} fullWidth>
              <Button
                fullWidth
                variant={Button.variants.primary}
                text={translations.tk_modalNotifyMeNotifyMeButton}
                onClickHandler={handleNotifyMePressed}
                loading={loading}
                disabled={!isRecaptchaValid || !termsCheckbox}
                data-testing="NotifyMe_Button_ConfirmNotification"
              />
            </Container>
            <Link
              href={
                isAuthenticated
                  ? getRoute(
                      Routes.profile,
                      true,
                      false,
                      ['section=email'],
                      Fragments.account,
                      Sites.zyn
                    )
                  : getRoute(Routes.userLogin, false, true)
              }
              onClickHandler={
                isAuthenticated ? handleOnEditProfileClick : handleOnLoginClick
              }
              newTab={false}
              disabled={loading}
              data-testing="NotifyMe_Link_GoToProfile">
              <TextBody
                size={TextBody.sizes.s}
                {...htmlTranslation(
                  translations,
                  isAuthenticated
                    ? 'tk_modalNotifyMeEditLink'
                    : 'tk_modalNotifyMeLoginLink'
                )}
              />
            </Link>
          </Container>
        )}
      </Container>
    </Popup>
  )
}

export default ModalNotifyMe
